<template>
  <div>
    <b-row class="mb-5" align-v="center">
      <b-col>
        <h4>Pareceres</h4>
      </b-col>
      <b-col class="text-right">
        <b-button class="btn medium blue" @click="openCreateStatementModal" v-if="showButton">
          Novo Parecer
        </b-button>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <ul class="tutoring-list" v-if="statements.length > 0">
          <li
            v-for="(statement, index) in statements"
            :key="index"
            :class="{
              'current-user-statement': isCurrentUserStatement(statement),
            }"
          >
            <i class="statement-meta">
              <strong>{{
                isCurrentUserStatement(statement)
                  ? "Por mim"
                  : statement.user_name
              }}</strong>
              <span v-if="statement.professional_description">
                • {{ statement.professional_description }}
              </span>
              <time>• {{ formatDate(statement.created_at) }}</time>
            </i>
            <p style="white-space: pre-line">
              {{ statement.body }}
            </p>
            <p class="professional-info" v-if="statement.Professional">
              <strong>Profissional:</strong>
              {{ statement.Professional.name }} -
              {{ statement.Professional.specialty }}
            </p>

            <div
              class="files-container"
              v-if="statement.files && statement.files.length > 0"
            >
              <div
                class="file-item"
                v-for="(file, fileIndex) in statement.files"
                :key="fileIndex"
              >
                <a :href="file.link" target="_blank" class="file-link">
                  <b-icon icon="file-arrow-down" aria-hidden="true"></b-icon>
                  <span>{{ file.name }}</span>
                </a>
              </div>
            </div>

            <div class="btn-group" v-if="canEditStatement(statement)">
              <button @click="openEditStatementModal(statement)" v-if="showButton">Editar</button>
              <button @click="deleteStatement(statement.id)" v-if="showButton">Excluir</button>
            </div>
          </li>
        </ul>
        <b-row class="box-not-found" v-else>
          <ul class="message-not-found">
            <li>
              <p>Ainda não existem pareceres para esse aluno.</p>
            </li>
          </ul>
        </b-row>
      </b-col>
    </b-row>

    <b-modal id="modal-create-statement" hide-footer>
      <template #modal-title>Novo Parecer</template>
      <div class="d-block text-center">
        <b-form>
          <b-form-group label="Tipo de Profissional">
            <SelectSimple
              :options="professionals"
              :value="statementForm.professional_id"
              :callback="selectProfessional"
            />
          </b-form-group>
          <b-form-group
            label="Descrição do Parecer *"
            class="informative"
            label-for="form-statement"
          >
            <b-form-textarea
              id="form-statement"
              v-model="statementForm.body"
              placeholder="Escreva aqui o parecer"
              rows="5"
              required
            ></b-form-textarea>
          </b-form-group>
          <b-form-group>
            <UploadMultipleFiles
              label="Anexar Arquivos"
              v-model="statementForm.files"
              :allowMultiple="true"
            />
          </b-form-group>
          <b-form-group class="wrap-btn-form">
            <b-button class="btn small blue" @click="createStatement" v-if="showButton">
              Salvar
            </b-button>
          </b-form-group>
        </b-form>
      </div>
    </b-modal>

    <b-modal id="modal-edit-statement" hide-footer>
      <template #modal-title>Editar Parecer</template>
      <div class="d-block text-center">
        <b-form>
          <b-form-group label="Tipo de Profissional">
            <SelectSimple
              :options="professionals"
              :value="statementForm.professional_id"
              :callback="selectProfessional"
            />
          </b-form-group>
          <b-form-group
            label="Descrição do Parecer *"
            class="informative"
            label-for="form-statement-edit"
          >
            <b-form-textarea
              id="form-statement-edit"
              v-model="statementForm.body"
              placeholder="Escreva aqui o parecer"
              rows="5"
              required
            ></b-form-textarea>
          </b-form-group>
          <b-form-group>
            <div
              class="existing-files"
              v-if="statementForm.currentFiles.length > 0"
            >
              <label>Arquivos Atuais:</label>
              <div class="current-files-list">
                <div
                  v-for="(file, index) in statementForm.currentFiles"
                  :key="index"
                  class="current-file-item"
                >
                  <span class="file-name">{{ file.name }}</span>
                  <button
                    type="button"
                    @click="removeExistingStatementFile(index)"
                    class="btn-remove-file"
                  >
                    <b-icon icon="x-circle" aria-hidden="true"></b-icon>
                  </button>
                </div>
              </div>
            </div>
            <UploadMultipleFiles
              label="Anexar Novos Arquivos"
              v-model="statementForm.files"
              :allowMultiple="true"
            />
          </b-form-group>
          <b-form-group class="wrap-btn-form">
            <b-button class="btn small blue" @click="updateStatement" v-if="showButton">
              Atualizar
            </b-button>
          </b-form-group>
        </b-form>
      </div>
    </b-modal>
  </div>
</template>

<script>
import UploadMultipleFiles from "@/components/inputs/UploadMultipleFiles.vue";
import SelectSimple from "@/components/inputs/SelectSimple.vue";
import moment from "moment";
import httpRda from "../http-rda";

export default {
  name: "StatementsComponent",
  components: {
    UploadMultipleFiles,
    SelectSimple,
  },
  props: {
    studentId: {
      type: Number,
      required: true,
    },
    currentUserId: {
      type: Number,
      required: true,
    },
    showButton: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      statements: [],
      professionals: [],
      statementForm: {
        id: null,
        body: null,
        professional_id: null,
        files: [],
        currentFiles: [],
        filesToRemove: [],
      },
    };
  },
  methods: {
    formatDate(date) {
      return moment(date).format("DD/MM/YYYY [às] HH:mm");
    },

    async getProfessionals() {
      try {
        const response = await httpRda.get("/professionals");
        const rawProfessionals = response.data.result || response.data || [];
        // Mapear para o formato esperado pelo SelectSimple (id e label)
        this.professionals = rawProfessionals.map((prof) => ({
          id: prof.id,
          label: prof.name,
          ...prof,
        }));
      } catch (err) {
        this.professionals = [];
      }
    },

    async getStatements() {
      if (!this.studentId) {
        return;
      }

      try {
        const response = await httpRda.get(
          `/students/${this.studentId}/statements`
        );
        this.statements = response.data.statements;
      } catch (err) {
        this.statements = [];
      }
    },

    openCreateStatementModal() {
      this.statementForm = {
        id: null,
        body: null,
        professional_id: null,
        files: [],
        currentFiles: [],
        filesToRemove: [],
      };
      this.$bvModal.show("modal-create-statement");
    },

    async openEditStatementModal(statement) {
      this.statementForm = {
        id: statement.id,
        body: statement.body,
        professional_id: statement.professional_id
          ? {
              id: statement.professional_id,
              label: statement.professional_description,
            }
          : null,
        files: [],
        currentFiles: statement.files
          ? statement.files.map((file) => ({
              id: file.id,
              name: file.name,
              url: file.link,
            }))
          : [],
        filesToRemove: [],
      };
      this.$bvModal.show("modal-edit-statement");
    },

    removeExistingStatementFile(index) {
      const fileToRemove = this.statementForm.currentFiles[index];
      if (fileToRemove.id) {
        this.statementForm.filesToRemove.push(fileToRemove.id);
      }
      this.statementForm.currentFiles.splice(index, 1);
    },

    selectProfessional(data) {
      this.statementForm.professional_id = data;
    },

    async createStatement() {
      if (!this.statementForm.body) {
        this.$swal.fire({
          title: "Alerta",
          text: "Preencha a descrição do parecer",
          icon: "error",
          showConfirmButton: false,
          timer: 1500,
        });
        return;
      }

      try {
        const formData = new FormData();
        formData.append("body", this.statementForm.body);
        formData.append("student_id", this.studentId);
        formData.append("user_id", this.currentUserId);

        if (
          this.statementForm.professional_id &&
          this.statementForm.professional_id.id
        ) {
          formData.append(
            "professional_id",
            this.statementForm.professional_id.id
          );
        }

        if (this.statementForm.files && this.statementForm.files.length > 0) {
          this.statementForm.files.forEach((file) => {
            formData.append("files", file);
          });
        }

        await httpRda.post(`/students/${this.studentId}/statements`, formData);

        this.$swal.fire({
          title: "Sucesso",
          text: "Parecer registrado com sucesso.",
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
        });

        this.$bvModal.hide("modal-create-statement");
        await this.getStatements();
      } catch (err) {
        this.$swal.fire({
          title: "Alerta",
          text: err?.response?.data?.message || err.message,
          icon: "error",
          showConfirmButton: false,
          timer: 1500,
        });
      }
    },

    async updateStatement() {
      if (!this.statementForm.body) {
        this.$swal.fire({
          title: "Alerta",
          text: "Preencha a descrição do parecer",
          icon: "error",
          showConfirmButton: false,
          timer: 1500,
        });
        return;
      }

      try {
        const formData = new FormData();
        formData.append("body", this.statementForm.body);

        if (
          this.statementForm.professional_id &&
          this.statementForm.professional_id.id
        ) {
          formData.append(
            "professional_id",
            this.statementForm.professional_id.id
          );
        }

        if (this.statementForm.files && this.statementForm.files.length > 0) {
          this.statementForm.files.forEach((file) => {
            formData.append("files", file);
          });
        }

        if (
          this.statementForm.filesToRemove &&
          this.statementForm.filesToRemove.length > 0
        ) {
          this.statementForm.filesToRemove.forEach((fileId) => {
            formData.append("filesToRemove", fileId);
          });
        }

        await httpRda.patch(
          `/students/${this.studentId}/statements/${this.statementForm.id}`,
          formData
        );

        this.$swal.fire({
          title: "Sucesso",
          text: "Parecer atualizado com sucesso.",
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
        });

        this.$bvModal.hide("modal-edit-statement");
        await this.getStatements();
      } catch (err) {
        this.$swal.fire({
          title: "Alerta",
          text: err?.response?.data?.message || err.message,
          icon: "error",
          showConfirmButton: false,
          timer: 1500,
        });
      }
    },

    async deleteStatement(id) {
      this.$swal
        .fire({
          title: "Tem certeza?",
          text: "Você não poderá reverter isso!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#68c5b7",
          cancelButtonColor: "#d33",
          confirmButtonText: "Sim, excluir!",
          cancelButtonText: "Cancelar",
        })
        .then(async (result) => {
          if (result.isConfirmed) {
            try {
              await httpRda.delete(
                `/students/${this.studentId}/statements/${id}`
              );
              this.$swal.fire({
                title: "Excluído!",
                text: "Parecer excluído com sucesso.",
                icon: "success",
                showConfirmButton: false,
                timer: 1500,
              });
              await this.getStatements();
            } catch (err) {
              this.$swal.fire({
                title: "Erro!",
                text: err?.response?.data?.message || err.message,
                icon: "error",
                showConfirmButton: false,
                timer: 1500,
              });
            }
          }
        })
        .catch(() => {
          this.$swal.fire({
            title: "Erro!",
            icon: "error",
            text: "Não foi possível excluir!",
          });
        });
    },

    canEditStatement(statement) {
      return (
        statement.teacher_id === this.currentUserId ||
        statement.user_id === this.currentUserId ||
        statement.created_by === this.currentUserId
      );
    },

    isCurrentUserStatement(statement) {
      return statement.user_id === this.currentUserId;
    },
  },
  async mounted() {
    await this.getProfessionals();
    await this.getStatements();
  },
  watch: {
    studentId: {
      immediate: false,
      async handler(newVal) {
        if (newVal) {
          await this.getStatements();
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.tutoring-list {
  display: flex;
  flex-direction: column;
  row-gap: 32px;
  margin: 0;
  padding: 0;

  li {
    display: flex;
    flex-direction: column;
    row-gap: 8px;
    border-bottom: 2px solid #e6e6e6;
    padding-bottom: 32px;
    list-style: none;
    transition: all 0.2s ease-in-out;

    &.current-user-statement {
      background-color: #f9fafb;
      border-left: 3px solid #d1e7dd;
      padding: 16px;
      margin-left: -16px;
      padding-left: 13px;
      border-radius: 8px;
    }

    &:last-child {
      border-bottom: 0;
      padding-bottom: 0;
    }

    time {
      font-weight: 400;
      font-size: 14px;
      color: #666666;
    }

    i.statement-meta {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      font-style: normal;
      font-size: 14px;
      color: #666666;
      margin-bottom: 8px;

      strong {
        color: #333;
        font-weight: 600;
      }

      time {
        font-weight: 400;
        font-size: 14px;
        color: #666666;
      }
    }

    p {
      margin-bottom: 0;

      &.professional-info {
        font-size: 14px;
        color: #666666;
        margin-top: 4px;

        strong {
          font-weight: 600;
        }
      }
    }

    .files-container {
      display: flex;
      gap: 8px;
      margin-top: 12px;
      margin-bottom: 12px;

      .file-item {
        .file-link {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          background: #fff;
          border: 1px solid #e9e9e9;
          border-radius: 6px;
          text-decoration: none;
          color: #333;
          cursor: pointer;
          transition: 0.2s all ease-in-out;

          &:hover {
            box-shadow: 3px 3px 0 rgba(0, 0, 0, 0.1);
            transform: translate(-3px, -3px);
          }

          span {
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
            max-width: 200px;
          }
        }
      }
    }

    .btn-group {
      gap: 10px;
      padding-top: 10px;

      button {
        font-weight: 600;
        font-size: 16px;
        color: #b23066;
        background-color: transparent;
        border: 0;
        cursor: pointer;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
}

.box-not-found {
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 100%;
}

.message-not-found {
  width: 50%;
  background: #fff;
  border: 1px solid #ededed;
  border-radius: 6px;
  padding: 10px;
  text-align: center;
  box-sizing: border-box;
  list-style: none;

  p {
    margin: 0;
  }
}

.existing-files {
  margin-bottom: 15px;

  label {
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
    display: block;
  }

  .current-files-list {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .current-file-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 12px;
      background-color: #f8f9fa;
      border: 1px solid #e9ecef;
      border-radius: 6px;

      .file-name {
        font-size: 14px;
        color: #495057;
        flex: 1;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }

      .btn-remove-file {
        background: none;
        border: none;
        color: #dc3545;
        cursor: pointer;
        padding: 4px;
        display: flex;
        align-items: center;

        &:hover {
          color: #a71d2a;
        }
      }
    }
  }
}
</style>
